import { Modal } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { FC, useMemo } from 'react'
import { gold, green, geekblue, purple, red } from '@ant-design/colors'
import styled from 'styled-components'
import { TableWithColumnManager } from '../../../../components/table-with-column-manager/table-with-column-manager'
import { LocalStorageKeys } from '../../../../constants/local-storage-keys'
import Shipment from '../../../../types/api/shipment'
import {
    formatPlanFactDelta,
    formatPlanFactValue,
    getShipmentPlanFactComparison,
    isPlanFactDeltaGood,
    PlanFactColumnGroup,
    PlanFactComparisonItem,
} from '../../helpers/get-shipment-plan-fact-comparison'

const GROUP_TITLES: Record<Exclude<PlanFactColumnGroup, 'general'>, string> = {
    money: 'Деньги',
    consumables: 'Расходники',
    time: 'Время',
}

const TableScroll = styled.div`
    overflow-x: auto;

    .ant-table-tbody > tr > td {
        white-space: nowrap;
        vertical-align: middle;
        height: 39px;
        box-sizing: border-box;
    }

    .ant-table-tbody > tr.plan-fact-row-planned > td {
        background: ${geekblue[1]};
    }

    .ant-table-tbody > tr.plan-fact-row-actual > td {
        background: ${purple[0]};
    }

    .ant-table-tbody > tr.plan-fact-row-delta > td {
        background: ${gold[0]};
    }

    .ant-table-tbody > tr.plan-fact-row-planned > td:first-child,
    .ant-table-tbody > tr.plan-fact-row-actual > td:first-child,
    .ant-table-tbody > tr.plan-fact-row-delta > td:first-child {
        font-weight: 600;
    }
`

const DeltaValue = styled.span<{ $good: boolean }>`
    font-weight: 600;
    color: ${props => (props.$good ? green[6] : red[5])};
`

const TextCell = styled.span`
    display: inline-block;
    max-width: 200px;
    overflow: hidden;
    text-overflow: ellipsis;
    vertical-align: bottom;
`

interface PlanFactComparisonModalProps {
    open: boolean
    shipment: Shipment
    onClose: () => void
}

type RowKey = 'planned' | 'actual' | 'delta'

interface ComparisonRow {
    key: RowKey
    rowLabel: string
    values: Record<string, string>
}

function metricColumn(item: PlanFactComparisonItem) {
    return {
        key: item.key,
        title: item.label,
        width: item.kind === 'text' ? 220 : 110,
        render: (_: unknown, record: ComparisonRow) => {
            const value = record.values[item.key] ?? '—'

            if (record.key === 'delta') {
                if (value === '—') return value
                return <DeltaValue $good={isPlanFactDeltaGood(item)}>{value}</DeltaValue>
            }

            if (item.kind === 'text') {
                return <TextCell title={value}>{value}</TextCell>
            }

            return value
        },
    }
}

const PlanFactComparisonModal: FC<PlanFactComparisonModalProps> = ({ open, shipment, onClose }) => {
    const items = useMemo(() => getShipmentPlanFactComparison(shipment), [shipment])

    const columns: ColumnsType<ComparisonRow> = useMemo(() => {
        const byGroup = (group: PlanFactColumnGroup) =>
            items.filter(item => item.group === group).map(metricColumn)

        return [
            {
                key: 'rowLabel',
                dataIndex: 'rowLabel',
                title: '',
                width: 90,
                fixed: 'left' as const,
            },
            ...byGroup('general'),
            { key: 'moneyGroup', title: GROUP_TITLES.money, children: byGroup('money') },
            {
                key: 'consumablesGroup',
                title: GROUP_TITLES.consumables,
                children: byGroup('consumables'),
            },
            { key: 'timeGroup', title: GROUP_TITLES.time, children: byGroup('time') },
        ]
    }, [items])

    const dataSource: ComparisonRow[] = useMemo(() => {
        const plannedValues: Record<string, string> = {}
        const actualValues: Record<string, string> = {}
        const deltaValues: Record<string, string> = {}

        for (const item of items) {
            plannedValues[item.key] = formatPlanFactValue(item, item.planned)
            actualValues[item.key] = formatPlanFactValue(item, item.actual)
            deltaValues[item.key] = formatPlanFactDelta(item) ?? '—'
        }

        return [
            { key: 'planned', rowLabel: 'План', values: plannedValues },
            { key: 'actual', rowLabel: 'Факт', values: actualValues },
            { key: 'delta', rowLabel: 'Разница', values: deltaValues },
        ]
    }, [items])

    return (
        <Modal
            open={open}
            onCancel={onClose}
            footer={null}
            destroyOnClose
            width={1100}
            title={`Сравнение: отгрузка №${shipment.id}`}
        >
            <TableScroll>
                <TableWithColumnManager
                    storageKey={LocalStorageKeys.ShipmentsPlanFactComparisonColumns}
                    size="small"
                    bordered
                    pagination={false}
                    columns={columns}
                    dataSource={dataSource}
                    scroll={{ x: 'max-content' }}
                    rowKey="key"
                    onRow={record => ({ className: `plan-fact-row-${record.key}` })}
                />
            </TableScroll>
        </Modal>
    )
}

export default PlanFactComparisonModal
